<script setup>
// NOTICE: KAPTION-INTEGRATION
// partner-brand-themes.SETTINGS.1-3 — which brand (logo) the account shows.
// Only rendered when the account can choose between more than one theme
// (rumo + themes of its partners); the server re-validates the choice. The
// General settings route itself is administrator-only.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import {
  DEFAULT_BRAND_THEME,
  brandThemeName,
  getBrandTheme,
} from 'dashboard/helper/kaptionBrandThemes';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const selectedTheme = ref(DEFAULT_BRAND_THEME);
const isSubmitting = ref(false);

const availableThemes = computed(
  () => currentAccount.value?.available_brand_themes || []
);
const isVisible = computed(() => availableThemes.value.length > 1);
const options = computed(() =>
  availableThemes.value.map(key => ({ value: key, label: brandThemeName(key) }))
);
const preview = computed(() => getBrandTheme(selectedTheme.value));

watch(
  () => currentAccount.value?.brand_theme,
  theme => {
    selectedTheme.value = theme || DEFAULT_BRAND_THEME;
  },
  { immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount({ brand_theme: selectedTheme.value }, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND_THEME.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND_THEME.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ isVisible, selectedTheme, handleSubmit });
</script>

<template>
  <SectionLayout
    v-if="isVisible"
    :title="t('GENERAL_SETTINGS.FORM.BRAND_THEME.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.BRAND_THEME.DESCRIPTION')"
    with-border
  >
    <form class="grid gap-4" @submit.prevent="handleSubmit">
      <label class="grid gap-1 text-sm text-n-slate-12">
        <span class="font-medium">
          {{ t('GENERAL_SETTINGS.FORM.BRAND_THEME.LABEL') }}
        </span>
        <select
          v-model="selectedTheme"
          data-test-id="brand-theme-select"
          class="!mb-0 text-sm"
        >
          <option
            v-for="option in options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <div
        v-if="preview.mark"
        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <img
          :src="preview.mark.light"
          :alt="preview.name"
          class="size-8 dark:hidden"
        />
        <img
          :src="preview.mark.dark"
          :alt="preview.name"
          class="hidden size-8 dark:block"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('GENERAL_SETTINGS.FORM.BRAND_THEME.PREVIEW_NOTE') }}
        </span>
      </div>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="t('GENERAL_SETTINGS.FORM.BRAND_THEME.UPDATE_BUTTON')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
